import type { SingleEntityDefinition, EventEntityDefinition, ActionDefinition, ModuleContext } from '@gzl10/nexus-sdk'
import type { Context } from 'hono'
import { useIdField, useSelectField, useTextField, useTextareaField, useSwitchField, useNumberField, useDatetimeField } from '@gzl10/nexus-sdk/fields'
import { getLoggerConfig } from '@kernel/logger/config.js'
import { getLoggerService } from '@kernel/logger/service.js'
import { generateId } from '../../core/utils/id.js'

/**
 * Single Entity: config
 * Shows the current logger configuration (read-only).
 * Defaults come from env vars; persisted as single (KV) for consistency with other config entities.
 */
export const loggerConfigEntity: SingleEntityDefinition = {
  type: 'config',
  key: 'logger_config',
  label: { en: 'Logger Config', es: 'Configuración del registro' },
  routePrefix: '/config',
  timestamps: true,
  order: 10,

  defaults: {
    level: 'info',
    format: 'pretty',
    sentry_enabled: false,
    sentry_environment: 'development',
    sentry_sample_rate: 1.0
  },

  envMapping: {
    level: { env: 'LOG_LEVEL' },
    format: { env: 'LOG_FORMAT' },
    sentry_enabled: { env: 'SENTRY_DSN' },
    sentry_environment: { env: 'NODE_ENV' },
    sentry_sample_rate: { env: 'SENTRY_SAMPLE_RATE', parse: 'number' }
  },

  fields: {
    id: useIdField(),
    level: useSelectField({
      label: { en: 'Log Level', es: 'Nivel de registro' },
      hint: { en: 'Env default: LOG_LEVEL', es: 'Env por defecto: LOG_LEVEL' },
      options: [
        { value: 'silent', label: { en: 'Silent (no logs)', es: 'Silencio (sin logs)' } },
        { value: 'fatal', label: { en: 'Fatal', es: 'Fatal' } },
        { value: 'error', label: { en: 'Error', es: 'Error' } },
        { value: 'warn', label: { en: 'Warn', es: 'Advertencia' } },
        { value: 'info', label: { en: 'Info', es: 'Información' } },
        { value: 'debug', label: { en: 'Debug', es: 'Depuración' } },
        { value: 'trace', label: { en: 'Trace', es: 'Rastreo' } }
      ],
      nullable: false
    }),
    format: useSelectField({
      label: { en: 'Format', es: 'Formato' },
      hint: { en: 'Env default: LOG_FORMAT', es: 'Env por defecto: LOG_FORMAT' },
      options: [
        { value: 'pretty', label: { en: 'Pretty (development)', es: 'Bonito (desarrollo)' } },
        { value: 'json', label: { en: 'JSON (production)', es: 'JSON (producción)' } }
      ],
      nullable: false
    }),
    sentry_enabled: useSwitchField({
      label: { en: 'Sentry Enabled', es: 'Sentry habilitado' },
      hint: { en: 'Env: SENTRY_DSN (optional)', es: 'Env: SENTRY_DSN (opcional)' }
    }),
    sentry_environment: useTextField({
      label: { en: 'Sentry Environment', es: 'Entorno de Sentry' },
      hint: { en: 'Env default: NODE_ENV', es: 'Env por defecto: NODE_ENV' },
      size: 50,
      nullable: true,
      meta: { showInDisplay: false }
    }),
    sentry_sample_rate: useNumberField({
      label: { en: 'Sentry Sample Rate', es: 'Tasa de muestreo de Sentry' },
      hint: { en: 'Env default: SENTRY_SAMPLE_RATE (1.0)', es: 'Env por defecto: SENTRY_SAMPLE_RATE (1.0)' },
      nullable: true,
      meta: { showInDisplay: false }
    })
  },

  casl: {
    subject: 'LoggerConfig',
    permissions: {
      ADMIN: { actions: ['read'] },
      SUPPORT: { actions: ['read'] }
    }
  }
}

/**
 * Action: health
 * GET /logger/health - Logging service status
 *
 * - No auth: basic status only (for external health checks)
 * - ADMIN auth: full configuration info
 */
export const healthAction: ActionDefinition = {
  key: 'health',
  label: { en: 'Health Check', es: 'Verificación de salud' },
  icon: 'mdi:heart-pulse',
  scope: 'module',
  method: 'GET',
  skipAuth: true,
  output: {},
  handler: async (_ctx, _input, c?: Context<any>) => {
    const config = getLoggerConfig()
    const logger = getLoggerService()

    // Info básica para todos (healthchecks)
    const basicInfo = {
      status: 'ok',
      level: config.level
    }

    // Info completa solo para admins autenticados
    const user = c?.get('user') as { id?: string } | undefined
    const ability = c?.get('ability') as { can?: (a: string, s: string) => boolean } | undefined
    if (user && ability?.can?.('manage', 'all')) {
      return {
        ...basicInfo,
        format: config.format,
        sentry: {
          enabled: logger.isSentryEnabled(),
          environment: config.sentry?.environment ?? null
        }
      }
    }

    return basicInfo
  }
}

// ============================================================================
// Client Errors (event entity + ingestion action)
// ============================================================================

/**
 * Event Entity: client_errors
 * Append-only log of errors reported by the frontend (UI/client).
 * Visible in Nexus admin panel for ADMIN/SUPPORT roles.
 */
export const clientErrorsEntity: EventEntityDefinition = {
  type: 'event',
  immutable: true,
  table: 'logger__client_errors',
  label: { en: 'Client Errors', es: 'Errores del cliente' },
  labelPlural: { en: 'Client Errors', es: 'Errores del cliente' },
  labelField: 'message',
  routePrefix: '/client-errors',
  retention: { days: 90 },
  availableDisplayModes: ['table', 'list', 'calendar'],
  calendarFrom: 'created_at',

  fields: {
    id: useIdField(),
    level: {
      ...useSelectField({
        label: { en: 'Level', es: 'Nivel' },
        required: true,
        options: [
          { value: 'error', label: { en: 'Error', es: 'Error' } },
          { value: 'fatal', label: { en: 'Fatal', es: 'Fatal' } }
        ],
        meta: { sortable: true, searchable: true }
      }),
      validation: { enum: ['error', 'fatal'] }
    },
    tag: useTextField({
      label: { en: 'Tag', es: 'Etiqueta' },
      size: 100,
      nullable: true,
      meta: { searchable: true, sortable: true }
    }),
    message: useTextareaField({
      label: { en: 'Message', es: 'Mensaje' },
      required: true,
      meta: { searchable: true, showInDisplay: false }
    }),
    url: useTextField({
      label: { en: 'URL', es: 'URL' },
      size: 2048,
      nullable: true,
      meta: { showInDisplay: false }
    }),
    user_agent: useTextareaField({
      label: { en: 'User Agent', es: 'Agente de usuario' },
      nullable: true,
      meta: { exportable: false, showInDisplay: false }
    }),
    ip_address: useTextField({
      label: { en: 'IP Address', es: 'Dirección IP' },
      size: 45,
      nullable: true,
      meta: { searchable: true, sortable: true }
    }),
    created_at: useDatetimeField({
      label: { en: 'Date', es: 'Fecha' },
      disabled: true,
      nullable: false,
      meta: { sortable: true }
    })
  },

  defaultSort: { field: 'created_at', order: 'desc' },

  casl: {
    subject: 'LoggerClientError',
    permissions: {
      ADMIN: { actions: ['read'] },
      SUPPORT: { actions: ['read'] }
    }
  }
}

/**
 * Action: report-client-error
 * POST /logger/client-errors/report - Receives error reports from the frontend.
 *
 * - skipAuth: true (frontend may not have a valid token at time of error)
 * - Rate limited: 10 requests/min per IP
 * - Always responds 204 (no info leak)
 */
export const reportClientErrorAction: ActionDefinition = {
  key: 'report-client-error',
  label: { en: 'Report Client Error', es: 'Reportar error del cliente' },
  icon: 'mdi:alert-circle-outline',
  scope: 'module',
  method: 'POST',
  skipAuth: true,
  successStatus: 204,
  output: {},
  middleware: (ctx: ModuleContext) => ctx.core.middleware.rateLimit({ windowMs: 60_000, max: 10, message: 'Too many error reports' }),

  handler: async (ctx, input, c?: Context<any>) => {
    const body = input as Record<string, unknown>
    const fwd = c?.req.header('x-forwarded-for')
    const ip = fwd?.split(',')[0]?.trim() || c?.req.header('x-real-ip') || 'unknown'

    // Validate required fields
    const message = typeof body['message'] === 'string' ? (body['message'] as string).slice(0, 5000) : null
    if (!message) {
      return {}
    }

    const level = body['level'] === 'fatal' ? 'fatal' : 'error'
    const tag = typeof body['tag'] === 'string' ? (body['tag'] as string).slice(0, 100) : null
    const url = typeof body['url'] === 'string' ? (body['url'] as string).slice(0, 2048) : null
    const userAgent = typeof body['userAgent'] === 'string' ? (body['userAgent'] as string).slice(0, 1000) : null

    // Insert into event entity via runtime service
    try {
      const { knex, t } = ctx.db
      await knex(t('logger__client_errors')).insert({
        id: generateId(),
        level,
        tag,
        message,
        url,
        user_agent: userAgent,
        ip_address: ip,
        created_at: ctx.db.nowTimestamp(knex)
      })
    } catch {
      // Silently fail — don't let client error reporting break anything
    }

    // Also log via pino so Sentry captures it if enabled
    const pinoLogger = getLoggerService()
    pinoLogger.error({ source: 'client', tag, url, ip }, `[Client] ${message}`)

    return {}
  }
}
